import { useEffect, useState } from 'react';
import { api, describeError } from './api.js';
import { disablePush, enablePush, pushState, type PushState } from './push.js';

function deviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Mac OS/.test(ua)) return 'Mac';
  return 'This device';
}

/** Turn push notifications on/off for this device, and send a test. */
export function NotificationsSettings() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void pushState().then(setState);
  }, []);

  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setState(await pushState());
      setMessage(done);
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h3>Notifications</h3>
      <p className="muted small">
        Approvals waiting, background tasks finished, and spending alerts.
      </p>
      {state === 'needs-install' && (
        <p className="small">
          On iPhone, first add Alfred to your Home Screen (Share → Add to Home Screen), then open it
          from there and turn notifications on.
        </p>
      )}
      {state === 'unsupported' && (
        <p className="small">This browser can’t receive notifications.</p>
      )}
      {state === 'denied' && (
        <p className="small">
          Notifications are blocked for Alfred. Allow them in this browser’s site settings, then
          come back here.
        </p>
      )}
      {state === 'off' && (
        <button
          className="btn btn-wide btn-primary"
          disabled={busy}
          onClick={() =>
            void run(() => enablePush(deviceName()), 'Notifications are on for this device.')
          }
        >
          Turn on for this device
        </button>
      )}
      {state === 'on' && (
        <div className="row-gap">
          <button
            className="btn btn-small"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const res = await api.pushTest();
                if (res.sent === 0) throw new Error('No device received it.');
              }, 'Test sent. It should arrive in a moment.')
            }
          >
            Send a test
          </button>
          <button
            className="btn btn-small"
            disabled={busy}
            onClick={() => void run(disablePush, 'Notifications are off for this device.')}
          >
            Turn off
          </button>
        </div>
      )}
      {message && <p className="small">{message}</p>}
    </section>
  );
}
